<template>

<div class="list-view">

	<div :class="showDetail ? 'sub-header detail-sub-header' : 'sub-header'">

		<div v-if="!showDetail" class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div v-if="showDetail" class="detail-title">{{ listDetail.description }}</div>
		<div v-if="!showDetail" class="controls">
			<pagination :paging="paging" @updatePage="goToPage" />
		</div>
		<div></div>
		<div v-if="showDetail && membership_id != 'new'" class="detail-title-meta"><label>ID: </label>{{ membership_id }}</div>
	</div>

	<div class="filters">

		<div v-if="!showDetail">
			<label>Subscription Frequency</label>
			<select v-model="filters.subscription_frequency_id" @change="getList()">
				<option value="">Select...</option>
				<option v-for="(frequency, index) in subscriptionFrequencies" :value="frequency.subscription_frequency_id">{{ frequency.description }}</option>
			</select>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.internal_use_only ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('internal_use_only')"></span>
					<label>Hidden</label>
				</li>
			</ul>
			<div :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="hasFilters ? clearFilters() : null">Clear Filters</div>

		</div>

		<div v-if="!showDetail">
			<div class="button action" @click="addItem()">New Membership</div>
			<div class="divider"></div>
			<div :class="['extra-menu-item', { disabled: gettingExport }]" @click="doExport()">Export CSV</div>
			<div v-if="legacyLink">
				<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
			</div>
			<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload List</div>
			<div class="message">{{ exportMessage }}</div>

		</div>

		<div v-if="showDetail">
			<div :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save Membership</div>
			<div class="divider"></div>
			<div class="button neutral" @click="backToList()">Back To List</div>
			<div class="divider"></div>
			<div v-if="membership_id != 'new'" class="extra-menu-item" @click="showConfirmDeactivate = true">Deactivate Membership</div>
		</div>

	</div>

	<loading v-if="loading" class="loading"></loading>

	<div v-else-if="showDetail" class="detail">
		<div class="two-one-columns">
			<div class="section">
				<h2>Membership Info</h2>
				<div class="two-columns">
					<div>
						<label-required :label="'UPC'" :condition="listDetail.upc" />
						<input type="text" v-model="listDetail.upc">
					</div>
					<div>
						<label class="full">Number of customers allowed</label>
						<select v-model="listDetail.max_customers" style="width: 10em;">
							<option value="">Select...</option>
							<option v-for="i in 10" :value="i">{{ i }}</option>
						</select>
						<tool-tip content="You can limit the number of customers allowed to share this membership. Leave blank for unlimited."></tool-tip>
					</div>
				</div>
				<label-required :label="'Description'" :condition="listDetail.description" />
				<input type="text" v-model="listDetail.description">
				<div class="three-columns">
					<div>
						<label>Cost</label>
						<number-input :decimals='2' v-model="listDetail.prices.cost" :key="'cost_' + listDetail.product_id" />
					</div>
					<div>
						<label-required :label="'Price'" :condition="listDetail.prices.pos" />
						<number-input :decimals='2' v-model="listDetail.prices.pos" :key="'pos_' + listDetail.product_id" />
					</div>
					<div>
						<label-required :label="'Frequency'" :condition="listDetail.subscription_frequency_id" />
						<select v-model="listDetail.subscription_frequency_id">
							<option value="">Select...</option>
							<option v-for="(frequency, index) in subscriptionFrequencies" :value="frequency.subscription_frequency_id">{{ frequency.description }}</option>
						</select>
					</div>
				</div>
				<div class="two-columns">
					<checkbox v-model="listDetail.not_taxable" label="Membership is not subject to sales tax" :key="'not_taxable_' + listDetail.not_taxable" class="v-spaced"/>
					<checkbox v-model="listDetail.internal_use_only" label="Hide this membership from my website" :key="'internal_use_only_' + listDetail.internal_use_only" class="v-spaced"/>
					<checkbox v-model="listDetail.member_cancel_allowed" label="Members can cancel this membership online" :key="'member_cancel_allowed_' + listDetail.member_cancel_allowed" class="v-spaced"/>
				</div>
			</div>
			<div class="section">
				<h2>Range Discounts</h2>
				<checkbox v-model="rangeDiscountFull" label="Members do not pay for range time" class="v-spaced" :key="'df_' + rangeDiscountFull"/>
				<checkbox v-model="rangeDiscountPercent" label="Members get a discount on range time" class="v-spaced" :key="'dp_' + rangeDiscountPercent"/>
				<div v-if="rangeDiscountPercent" style="margin: 0 0 0 2em;">
					<label>Enter decimal, e.g. 0.5 = 50%.</label>
					<number-input :decimals='2' v-model="listDetail.range_discount" :key="'dp_' + listDetail.range_discount" /> 
				</div>
				<checkbox v-model="rangeDiscountAmount" label="Members pay a special amount for range time" class="v-spaced" :key="'da_' + rangeDiscountAmount"/>
				<div v-if="rangeDiscountAmount" style="margin: 0 0 0 2em;">
					<label>Enter dollar amount.</label>
					<number-input :decimals='2' v-model="listDetail.range_amount" :key="'da_' + listDetail.range_amount" />
				</div>
			</div>
			<div v-if="false" class="section">
				<h2>Product Discounts</h2>
				<checkbox v-model="productDiscountPercent" label="Members get a discount on products" class="v-spaced" :key="'pd_' + productDiscountPercent"/>
				<div v-if="productDiscountPercent" style="margin: 0 0 0 2em;">
					<label>Enter decimal, e.g. 0.1 = 10%.</label>
					<number-input :decimals='2' v-model="listDetail.product_discount" :key="'pd_' + listDetail.product_discount" /> 
				</div>
				<div v-if="productDiscountPercent">
					<checkbox v-model="productDiscountIncludesDepartments" label="Product discount only applies to certain departments..." class="v-spaced" :key="'pdi_' + productDiscountIncludesDepartments"/>
					<div v-if="productDiscountIncludesDepartments" style="margin: 0 0 0 2em;">
						<select v-model="selectedDepartment" @change="addToDepartmentList('include')" style="width: 18em;">
							<option value="">Select...</option>
							<option v-for="department in departments" :value="department.department_id">{{ department.description }}</option>
						</select>
						<div class="three-columns bordered" style="padding: 0 0.5em;">
							<div v-for="department in listDetail.discount_departments_include" class="v-spaced">
								<div class="button neutral inline spaced" @click="removeFromDepartmentList('include', department.department_id)"><i class="fa fa-times"></i></div>
								{{ department.description }}
							</div>
						</div>
					</div>
					<checkbox v-model="productDiscountExcludesDepartments" label="Product discount applies to all departments except..." class="v-spaced" :key="'pde_' + productDiscountExcludesDepartments"/>
					<div v-if="productDiscountExcludesDepartments" style="margin: 0 0 0 2em;">
						<select v-model="selectedDepartment" @change="addToDepartmentList('exclude')" style="width: 18em;">
							<option value="">Select...</option>
							<option v-for="department in departments" :value="department.department_id">{{ department.description }}</option>
						</select>
						<div class="three-columns bordered" style="padding: 0 0.5em;">
							<div v-for="department in listDetail.discount_departments_exclude" class="v-spaced">
								<div class="button neutral inline spaced" @click="removeFromDepartmentList('exclude', department.department_id)"><i class="fa fa-times"></i></div>
								{{ department.description }}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="section">
				<h2>Members</h2>
				<label class="full">There are currently <b>{{ listDetail.member_count }}</b> member{{ listDetail.member_count == 1 ? '' : 's' }} with {{ listDetail.description }}.</label>
				<router-link :to="{ path: '/range-memberships/members', query: { membership_id: listDetail.membership_id } }" class="button neutral small v-spaced">Manage Members</router-link>
			</div>
		</div>

	</div>

	<div v-else class="list">

		<div v-if="list.length == 0" class="empty-list">
			This list is empty
		</div>

		<table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Membership</th>
					<th class="list">UPC</th>
					<th class="list">Frequency</th>
					<th class="list center">Range Discount</th>
					<th class="list center">Hidden</th>
				</tr>
			</thead>
			<tr v-for="(membership, index) in list" class="clickable-list-item" @click="getItemRoute(membership.membership_id)">
				<td class="list">{{ membership.description }}</td>
				<td class="list">{{ membership.upc }}</td>
				<td class="list">{{ membership.subscription_frequency_code.capitalizeWords() }}</td>
				<td class="list center"><i v-if="membership.range_amount || membership.range_discount" class='fa fa-check'></i></td>
				<td class="list center"><i v-if="membership.internal_use_only" class='fa fa-check'></i></td>
			</tr>
		</table>

	</div>

	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<div v-if="showConfirmChangePrice" class="modal">
		<div class="inner">
			<h2>Change Membership Price</h2>
			<div class="content">
				<p>There are currently {{ listDetail.member_count }} members with a subscription for this membership type.</p>
				<p>These subscriptions will use the new price of ${{ listDetail.prices.pos }} starting with their next scheduled payment.</p>
				<p>Are you sure you want to update the membership price?</p>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showConfirmChangePrice = false">Cancel</div>
				<div class="button neutral spaced" @click="setChangeSubscriptions('price')">Yes, update</div>
			</div>
		</div>
	</div>

	<div v-if="showConfirmDeactivate" class="modal">
		<div class="inner">
			<h2>Deactivate Membership</h2>
			<div class="content">
				<p>Deactivating this membership will hide it from your website and POS.</p>
				<div v-if="listDetail.member_count > 0">
					<p>There are currently {{ listDetail.member_count }} members with a subscription for this membership type.</p>
					<p>Deactivating it will also suspend their payments.</p>
				</div>
				<div v-else>
					<p>Are you sure you want to deactivate this membership?</p>
				</div>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showConfirmDeactivate = false">Cancel</div>
				<div class="button neutral spaced" @click="setChangeSubscriptions('inactive')">Yes, deactivate</div>
			</div>
		</div>
	</div>

	<help-popup :helpLink="{398:'Membership Setup', 399:'Membership Application Pages', 60:'Online Membership Application', 409:'Selling Memberships at POS', 400:'Manage Members', 403:'Customer Kiosk'}" :tours="false"></help-popup>

</div>

</template>

<script>
import Loading from '@/components/Loading.vue'
import LabelRequired from '@/components/LabelRequired.vue'
import NumberInput from '@/components/NumberInput.vue'
import Checkbox from '@/components/Checkbox.vue'
import ToolTip from '@/components/ToolTip.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'memberships',
	props: ['membership_id'],
	components: {
		'loading': Loading,
		'label-required': LabelRequired,
		'number-input': NumberInput,
		'checkbox': Checkbox,
		'tool-tip': ToolTip,
		'help-popup': HelpPopup
	},
	data() {
		return {
			loading: false,
			saving: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			filters: {},
			filterMessage: '',
			filterLoading: false,
			subscriptionFrequencies: [],
			list: [],
			paging: {},
			showDetail: false,
			listDetail: {},
			listIndex: '',
			rangeDiscountFull: false,
			rangeDiscountPercent: false,
			rangeDiscountAmount: false,
			departments: [],
			productDiscountPercent: false,
			productDiscountIncludesDepartments: false,
			productDiscountExcludesDepartments: false,
			selectedDepartment: '',
			currentPrices: {},
			confirmChangeSubscriptions: false,
			showConfirmChangePrice: false,
			updateSubscriptions: false,
			gettingExport: false,
			exportMessage: '',
			modalMessage: '',
			showConfirmDeactivate: false,
			legacyLink: ''
		}
	},
	created() {
		this.getListSubscriptionFrequencies()
		this.clearFilters()
	},
	computed: {
		hasFilters() {
			return this.filters.search_term || this.filters.subscription_frequency_id || this.filters.inactive
		},
		canSave() {
			return this.listDetail.description && this.listDetail.upc && this.listDetail.subscription_frequency_id 
		}
	},
	watch: {
		contact_membership_id: function() {
			this.getItem(this.contact_membership_id)
		},
		rangeDiscountFull: function() {
			if (this.rangeDiscountFull) {
				this.rangeDiscountPercent = false
				this.rangeDiscountAmount = false
			}
		},
		rangeDiscountPercent: function() {
			if (this.rangeDiscountPercent) {
				this.rangeDiscountFull = false
				this.rangeDiscountAmount = false
			}
		},
		rangeDiscountAmount: function() {
			if (this.rangeDiscountAmount) {
				this.rangeDiscountFull = false
				this.rangeDiscountPercent = false
			}
		},
		productDiscountIncludesDepartments: function() {
			if (this.productDiscountIncludesDepartments) {
				this.productDiscountExcludesDepartments = false
				this.listDetail.discount_departments_exclude = []
			}
		},
		productDiscountExcludesDepartments: function() {
			if (this.productDiscountExcludesDepartments) {
				this.productDiscountIncludesDepartments = false
				this.listDetail.discount_departments_include = []
			}
		}
	},
	methods: {
		getListSubscriptionFrequencies() {
			this.apiCall({ destination: 'get_list_subscription_frequencies' }).then(obj => {
				if (obj.status == 'success') {
					this.subscriptionFrequencies = obj.subscription_frequencies
				}
			}).catch(err => {
				console.log(err)
			})
		},
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				subscription_frequency_id: '',
				sort: '',
				inactive: 0,
				do_export: 0
			}
			if (this.membership_id) {
				this.getItem(this.membership_id)
			} else {
				this.getList()
			}
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.filters.page = 1
			this.getList()
		},
		getList() {
			if (this.loading) { return }
			this.showDetail = false;
			this.loading = true
			this.apiCall({ destination: 'get_list_memberships', data: this.filters }).then(obj => {
				this.loading = false
				if (obj.legacy_link) {
					this.legacyLink = obj.legacy_link
				}
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 1000)
				} else {
					if (obj.status == 'success') {
						this.list = obj.memberships
						if (obj.paging) {
							this.paging = obj.paging
						}
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		getItemRoute(membershipId) {
			this.$router.push('/range-memberships/memberships/' + membershipId)
			this.getItem(membershipId)
		},
		getDepartments() {
			this.apiCall({ destination: 'get_list_departments' }).then(obj => {
				this.departments = obj.departments
			}).catch(err => {
				console.log(err)
			})
		},
		getItem(membership) {
			if (this.loading) { return }
			this.loading = true
			let membershipId = ''
			if (typeof membership === 'object') {
				membershipId = membership.membership_id
			} else {
				membershipId = membership
			}
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i].membership_id == membershipId) {
					this.listIndex = i
					break
				}
			}
			this.apiCall({ destination: 'get_item_membership', data: { membership_id: membershipId } }).then(obj => {
				this.loading = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.listDetail = obj.membership
					this.currentPrices = JSON.parse(JSON.stringify(this.listDetail.prices))
					this.listDetail.range_discount_full = false
					this.listDetail.range_discount_percent = false
					this.listDetail.range_discount_amount = false
					if (this.listDetail.range_discount) {
						if (this.listDetail.range_discount < 1) {
							this.rangeDiscountPercent = true
						} else {
							this.rangeDiscountFull = true
						}
					} else if (this.listDetail.range_amount) {
						this.rangeDiscountAmount = true
					}
					if (this.departments.length == 0) {
						this.getDepartments()
					}
					this.showDetail = true;
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addToDepartmentList(which) {
			let inList = false
			if (which == 'include') {
				this.listDetail.discount_departments_include.forEach((department) => {
					if (department.department_id == this.selectedDepartment) {
						inList = true
						return
					}
				})
			} else {
				this.listDetail.discount_departments_exclude.forEach((department) => {
					if (department.department_id == this.selectedDepartment) {
						inList = true
						return
					}
				})
			}
			if (inList) { return }
			this.departments.forEach((department) => {
				if (department.department_id == this.selectedDepartment) {
					if (which == 'include') {
						this.listDetail.discount_departments_include.push(department)
					} else {
						this.listDetail.discount_departments_exclude.push(department)
					}
				}
			})
			this.selectedDepartment = ''
		},
		removeFromDepartmentList(which, departmentId) {
			if (which == 'include') {
				this.listDetail.discount_departments_include.forEach((department, index) => {
					if (department.department_id == departmentId) {
						this.listDetail.discount_departments_include.splice(index, 1)
						return
					}
				})
			} else {
				this.listDetail.discount_departments_exclude.forEach((department, index) => {
					if (department.department_id == departmentId) {
						this.listDetail.discount_departments_exclude.splice(index, 1)
						return
					}
				})
			}
		},
		addItem() {
			this.listDetail = {
				description: '',
				range_discount: '',
				range_amount: '',
				product_discount: '',
				internal_use_only: 0,
				inactive: 0,
				prices: {}
			}
			this.$router.push('/range-memberships/memberships/new')
			this.showDetail = true
		},
		saveItem() {
			if (this.saving || !this.canSave) { return }
			// if price has changed and if there are members, get confirmation
			if (this.listDetail.member_count > 0 && this.currentPrices.pos != this.listDetail.prices.pos && !this.confirmChangeSubscriptions) {
				this.showConfirmChangePrice = true
				return
			}
			// duplicate pos price for web price
			this.listDetail.prices.web = this.listDetail.prices.pos
			// translate checkbox values
			if (this.rangeDiscountFull) {
				this.listDetail.range_discount = '1'
				this.listDetail.range_amount = ''
			} else if (this.rangeDiscountPercent) {
				this.listDetail.range_amount = ''
			} else if (this.rangeDiscountAmount) {
				this.listDetail.range_discount = ''
			}
			this.listDetail.update_subscriptions = this.updateSubscriptions
			this.saving = true
			this.message = 'Saving membership'
			this.messageStatus = ''
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_membership', data: this.listDetail }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Membership saved!'
					this.messageTimeout = 1000
					this.confirmChangeSubscriptions = false
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setChangeSubscriptions(which) {
			if (which == 'price') {
				this.listDetail.inactive = false
				this.showConfirmChangePrice = false
			}
			if (which == 'inactive') {
				this.listDetail.inactive = true
				this.showConfirmDeactivate = false
			}
			this.confirmChangeSubscriptions = true
			this.updateSubscriptions = true
			this.saveItem()
		},
		confirmDeleteItem() {
			this.message = 'Are you sure you want to delete this membership?'
			this.messageStatus = 'delete'
		},
		saveDeleteItem() {
			if (this.saving || !this.canSave) { return }
			this.saving = true
			if (this.listDetail.inactive) {
				this.message = 'Undeleting membership'
			} else {
				this.message = 'Deleting membership'
			}
			this.messageStatus = ''
			this.messageTimeout = 0
			this.listDetail.inactive = !this.listDetail.inactive
			this.apiCall({ destination: 'save_item_membership_deleted', data: { membership_id: this.listDetail.membership_id, inactive: this.listDetail.inactive } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					if (this.listDetail.inactive) {
						this.message = 'Membership deleted'
					} else {
						this.message = 'Membership undeleted'
					}
					this.messageTimeout = 1500
					this.backToList()
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		doMessageModalAction() {
			switch (this.messageStatus) {
				case 'delete':
					this.saveDeleteItem()
					break;
			}
		},
		backToList() {
			this.listDetail = {}
			this.showDetail = false
			this.$router.push('/range-memberships/memberships')
			this.getList()
		},
		doExport() {
			if (this.gettingExport) {
				return false
			}
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.paging.total_items + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		}
	}
}
</script>
